import React from "react";
import { useSelector } from "react-redux";
import { Box, Typography, Link as MuiLink, Button } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { FormattedMessage } from "react-intl";
import { truncate, getFirstPathParam } from "bundles/common/utils/utils";
import HTML from "bundles/common/components/HTML";
import moment from "moment";

const useStyles = makeStyles((theme) => ({
  grid: {
    display: "grid",
    gap: 32,
    gridTemplateColumns: "minmax(460px, 1fr) 1fr",
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "1fr",
    },
  },
  imageWrapper: {
    overflow: "hidden",
    borderRadius: 8,
  },
  image: {
    width: "100%",
    height: "100%",
    maxHeight: 400,
    objectFit: "cover",
    transition: "transform 0.5s ease",
    "&:hover": {
      transform: "scale(1.05)",
    },
  },
  content: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    gap: 16,
  },
  date: {
    fontSize: "0.875rem !important",
    color: "hsl(220 10% 46%)",
  },
  title: {
    fontSize: "1.5rem !important",
    fontFamily: "Segoe UI Semibold, system-ui, sans-serif !important",
    lineHeight: 1.25,
    color: "hsl(220 40% 13%)",
    [theme.breakpoints.up("md")]: {
      fontSize: "1.875rem !important",
    },
  },
  excerpt: {
    lineHeight: 1.625,
    color: "hsl(220 10% 46%)",
  },
  authorRow: {
    fontSize: "0.875rem !important",
    color: theme.palette.text.secondary,
  },
  authorName: {
    fontFamily: "Segoe UI Semibold, system-ui, sans-serif !important",
    color: "hsl(152 60% 44%)",
  },
  readButton: {
    "&&&": {
      width: "fit-content",
      borderColor: "hsl(152 60% 44%)",
      color: "hsl(152 60% 44%)",
      textTransform: "uppercase",
      fontWeight: 600,
      fontSize: "0.8125rem",
      letterSpacing: "0.5px",
      borderRadius: 6,
      px: ".5rem !important",
      py: "1rem !important",
      "&:hover": {
        backgroundColor: "hsl(152 60% 44%)",
        color: "#fff",
      },
    },
  },
}));

const FeaturedArticle = ({ post, companyId }) => {
  const classes = useStyles();
  const i18nLocale = useSelector((state) => state.i18nLocale);

  return (
    <Box className={classes.grid}>
      <MuiLink
        href={`/${i18nLocale}/${getFirstPathParam()}/posts/${post.encoded_title}/${post.id}`}
        underline="none"
        className={classes.imageWrapper}
      >
        <img
          className={classes.image}
          src={post.cover_picture_url}
          alt={post.title}
          loading="eager"
        />
      </MuiLink>
      <Box className={classes.content}>
        <Typography className={classes.date}>
          {moment(post.created_at).format("DD/MM/YYYY")}
        </Typography>
        <Typography className={classes.title}>
          {truncate(post.title, 80)}
        </Typography>
        <Typography className={classes.excerpt} component="div">
          <HTML
            html={truncate(post.content, 200)}
            blacklistedTags={["strong", "p"]}
          />
        </Typography>
        <Typography className={classes.authorRow}>
          <FormattedMessage id="landing_candidate.from" />{" "}
          {companyId ? (
            <MuiLink
              href={`/companies/squarehub/${companyId}`}
              onClick={(e) => e.stopPropagation()}
              sx={{
                color: "inherit",
                textDecoration: "none",
                "&:hover": { color: "#035d2a" },
              }}
            >
              <span className={classes.authorName}>Squarehub</span>
            </MuiLink>
          ) : (
            <span className={classes.authorName}>Squarehub</span>
          )}
        </Typography>
        <Button
          variant="outlined"
          className={classes.readButton}
          href={`/${i18nLocale}/${getFirstPathParam()}/posts/${post.encoded_title}/${post.id}`}
        >
          <FormattedMessage id="blog.read_article" />
        </Button>
      </Box>
    </Box>
  );
};

export default FeaturedArticle;
